import React, { useEffect, useState } from 'react';
import { Box, Tab, Tabs, Typography, alpha } from '@mui/material';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import HomeLayout from '../../asset/layout/HomeLayout';
import { BrandManagement } from './BrandManagement';
import PremiumCardsManagement from './PremiumCardsManagement';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

const TAB_CONFIG = [
  { id: 'all', label: 'All brands', provider: 'ALL' },
  { id: 'woohoo', label: 'Woohoo', provider: 'WOOHOO', brandType: 'WOOHOO' },
  { id: 'gyftr', label: 'Gyftr', provider: 'Gyftr' },
  { id: 'premium', label: 'Premium cards' },
];

export default function Brand() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = (searchParams.get('tab') || '').toLowerCase();

  const getTabIndex = (param) => {
    if (param === 'woohoo') return 1;
    if (param === 'gyftr') return 2;
    if (param === 'premium' || param === 'premium-cards') return 3;
    return 0;
  };

  const [selectedTab, setSelectedTab] = useState(() => getTabIndex(tabParam));

  useEffect(() => {
    const nextTab = getTabIndex(tabParam);
    if (nextTab !== selectedTab) {
      setSelectedTab(nextTab);
    }
  }, [tabParam]);

  const handleTabChange = (_, newValue) => {
    setSelectedTab(newValue);
    const tabItem = TAB_CONFIG[newValue] || TAB_CONFIG[0];
    if (tabItem.id === 'all') {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('tab');
      setSearchParams(nextParams, { replace: true });
    } else {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', tabItem.id);
      setSearchParams(nextParams, { replace: true });
    }
  };

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: '100%',
          background:
            'linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)',
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: '24px',
              background:
                'linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)',
              color: '#fff',
              boxShadow: '0 20px 60px rgba(17, 64, 155, 0.35)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <StorefrontOutlinedIcon />
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, letterSpacing: '0.1em', opacity: 0.85 }}
              >
                CATALOG
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
              Brand management
            </Typography>
            <Typography sx={{ mt: 1, opacity: 0.82, maxWidth: 560 }}>
              Manage brand catalog, discounts, categories, stock, and premium
              card settings from one place.
            </Typography>
          </Box>
        </motion.div>

        <Box
          sx={{
            mb: 3,
            bgcolor: '#fff',
            borderRadius: '16px',
            border: `1px solid ${alpha('#11409B', 0.1)}`,
            boxShadow: '0 4px 24px rgba(15, 23, 42, 0.05)',
            px: 1,
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                color: '#64748b',
                minHeight: 48,
              },
              '& .Mui-selected': { color: '#11409B' },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                bgcolor: '#11409B',
              },
            }}
          >
            {TAB_CONFIG.map((t) => (
              <Tab key={t.id} label={t.label} />
            ))}
          </Tabs>
        </Box>

        {selectedTab === 0 && <BrandManagement provider="ALL" key="all" />}
        {selectedTab === 1 && <BrandManagement provider="WOOHOO" brandType="WOOHOO" key="woohoo" />}
        {selectedTab === 2 && <BrandManagement provider="Gyftr" key="gyftr" />}
        {selectedTab === 3 && <PremiumCardsManagement key="premium" />}
      </Box>
    </HomeLayout>
  );
}
